import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Download, FileArchive, History, Loader2, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRMATION = 'WIPE APPRAISAL KPP';

const errorMessage = (error, fallback) => error?.response?.data?.detail || fallback;
const formatSize = (bytes = 0) => `${Math.max(bytes / 1024, 0.1).toFixed(1)} KB`;

export const AppraisalDataSafety = ({ onWipeComplete }) => {
  const [status, setStatus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [wiping, setWiping] = useState(false);
  const [lastAuditId, setLastAuditId] = useState(null);

  const config = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/appraisal/admin/data-safety`, config());
      setStatus(response.data);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to load appraisal archive status'));
    }
  }, [config]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const downloadArchive = async (archive) => {
    setDownloadingId(archive.id);
    try {
      const response = await axios.get(`${API}/appraisal/admin/archives/${archive.id}/download`, {
        ...config(), responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = archive.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to download archive'));
    } finally {
      setDownloadingId(null);
    }
  };

  const createArchive = async () => {
    setExporting(true);
    try {
      const response = await axios.post(`${API}/appraisal/admin/archive`, {}, config());
      await downloadArchive(response.data.archive);
      await fetchStatus();
      toast.success('Excel + JSON archive created, verified, and downloaded');
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to create archive'));
    } finally {
      setExporting(false);
    }
  };

  const executeWipe = async () => {
    setWiping(true);
    try {
      const response = await axios.post(`${API}/appraisal/admin/wipe`, {
        confirmation,
        archive_id: status.latest_archive.id,
      }, config());
      setLastAuditId(response.data.audit_id);
      setConfirmOpen(false);
      setConfirmation('');
      await fetchStatus();
      onWipeComplete?.();
      toast.success('Appraisal cycles, forms, and KPPs were permanently wiped');
    } catch (error) {
      toast.error(errorMessage(error, 'Wipe failed; no further action was taken'));
    } finally {
      setWiping(false);
    }
  };

  if (!status) return <div className="h-24 animate-pulse rounded border bg-slate-100" data-testid="appraisal-data-safety-loading" />;

  const counts = status.counts || {};
  const canWipe = status.total_records > 0 && status.archive_current && status.latest_archive;

  return (
    <section className="space-y-6" data-testid="appraisal-data-safety-section">
      <div className="border border-blue-200 bg-blue-50/80 p-5 shadow-sm">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <FileArchive className="h-5 w-5 text-blue-600" /> Appraisal archive
            </div>
            <p className="mt-1 text-sm text-slate-600">Creates one verified ZIP with an Excel workbook and JSON files, stored securely for recovery.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge className={status.archive_current ? 'bg-emerald-600' : 'bg-amber-600'} data-testid="archive-status-badge">
              {status.archive_current ? 'Current archive verified' : 'Fresh archive required'}
            </Badge>
            <Button variant="outline" onClick={() => setHistoryOpen(!historyOpen)} data-testid="view-archive-history-button">
              <History className="mr-2 h-4 w-4" /> Archive history
            </Button>
            <Button onClick={createArchive} disabled={exporting} className="bg-blue-600 hover:bg-blue-700" data-testid="export-appraisal-archive-button">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Export Excel + JSON
            </Button>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            ['Appraisal cycles', counts.appraisal_cycles, 'summary-count-appraisal-cycles'],
            ['Appraisal forms', counts.appraisal_forms, 'summary-count-appraisal-forms'],
            ['Appraisal KPPs', counts.appraisal_kpps, 'summary-count-appraisal-kpps'],
          ].map(([label, value, testId]) => (
            <div key={label} className="border border-blue-200 bg-white px-4 py-3">
              <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900" data-testid={testId}>{value || 0}</p>
            </div>
          ))}
        </div>

        {historyOpen && (
          <div className="mt-5 border-t border-blue-200 pt-4" data-testid="archive-history-panel">
            <p className="mb-3 text-sm font-semibold text-slate-800">Recent archives</p>
            <div className="space-y-2">
              {status.archives.length === 0 && <p className="text-sm text-slate-500" data-testid="archive-history-empty">No archives yet.</p>}
              {status.archives.map((archive) => (
                <div key={archive.id} className="flex flex-col gap-2 border bg-white p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`archive-history-row-${archive.id}`}>
                  <div className="min-w-0 text-sm">
                    <p className="truncate font-medium text-slate-800">{archive.filename}</p>
                    <p className="text-xs text-slate-500">{new Date(archive.generated_at).toLocaleString()} · {formatSize(archive.size)} · {archive.generated_by_email}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadArchive(archive)} disabled={downloadingId === archive.id} data-testid={`archive-download-button-${archive.id}`}>
                    {downloadingId === archive.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Download
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="relative overflow-hidden border border-red-200 bg-red-50/50 p-5 shadow-sm" data-testid="danger-zone-section">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-bold text-red-700"><ShieldAlert className="h-5 w-5" /> Danger zone</h3>
            <p className="mt-1 max-w-3xl text-sm text-red-800">Permanently deletes only appraisal cycles, appraisal forms, and appraisal KPP definitions in the environment where this button is clicked.</p>
            {!canWipe && status.total_records > 0 && (
              <p className="mt-3 flex items-center gap-2 text-sm font-medium text-amber-800" data-testid="archive-required-warning">
                <AlertTriangle className="h-4 w-4" /> Export a fresh archive before wiping.
              </p>
            )}
            {status.total_records === 0 && (
              <p className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="zero-state-kpp-notice">
                <CheckCircle2 className="h-4 w-4" /> 0 records remaining. Appraisal KPP data is clear.
              </p>
            )}
            {lastAuditId && <Badge variant="outline" className="mt-3 font-mono" data-testid="post-wipe-audit-log-badge">Audit: {lastAuditId}</Badge>}
          </div>
          <Button variant="destructive" onClick={() => setConfirmOpen(true)} disabled={!canWipe} data-testid="wipe-production-kpp-button">
            <Trash2 className="mr-2 h-4 w-4" /> Wipe this environment
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="wipe-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Permanently wipe Appraisal KPP data?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes {status.total_records} records across appraisal cycles, forms, and KPPs. Archive <strong>{status.latest_archive?.filename}</strong> will remain available.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 border bg-slate-50 p-4">
            <Label htmlFor="appraisal-wipe-confirmation">Type <span className="font-mono font-bold text-red-700">{CONFIRMATION}</span></Label>
            <Input id="appraisal-wipe-confirmation" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" data-testid="confirm-wipe-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmation('')} data-testid="cancel-wipe-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeWipe} disabled={confirmation !== CONFIRMATION || wiping} className="bg-red-600 hover:bg-red-700" data-testid="execute-wipe-button">
              {wiping ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Permanently wipe
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};